import React, { useEffect, useMemo, useState } from 'react'
import { getCiclos, getCicloDetalle } from 'src/services/preciosService'
import TablaPrecios from './TablaPrecios'

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`
const pctVar = (v) => {
  const n = Number(v || 0)
  const color = n > 0 ? '#d64545' : n < 0 ? '#1f9d55' : '#6b7684'
  return <span style={{ color, fontWeight: 600 }}>{n > 0 ? '+' : ''}{n.toFixed(1)}%</span>
}

/**
 * Ciclos mensuales de precio.
 * Las actualizaciones en cantidad se dan el día 1 de cada mes (decisión del comité del último viernes),
 * así que cada mes es una tanda: acá se ve qué se movió, y si algo se corrigió después de la carga.
 */
const CiclosTab = () => {
  const [ciclos, setCiclos] = useState([])
  const [mes, setMes] = useState('')
  const [detalle, setDetalle] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const fetchCiclos = async () => {
    setIsLoading(true)
    const r = await getCiclos()
    const data = (r && r.data) || []
    setCiclos(data)
    if (!mes && data.length) setMes(data[0].mes)
    setIsLoading(false)
  }

  useEffect(() => {
    fetchCiclos()
  }, [])

  useEffect(() => {
    if (!mes) return
    let vivo = true
    getCicloDetalle(mes).then((r) => {
      if (vivo) setDetalle((r && r.data) || [])
    })
    return () => {
      vivo = false
    }
  }, [mes])

  const colsCiclos = useMemo(
    () => [
      { accessorKey: 'mes', header: 'Ciclo', size: 90 },
      { accessorKey: 'estado', header: 'Estado', size: 90 },
      { id: 'modelos', header: 'Modelos', size: 90, accessorFn: (r) => Number(r.modelos || 0) },
      { id: 'cambios', header: 'Cambios (colores)', size: 130, accessorFn: (r) => Number(r.cambios || 0) },
      {
        id: 'variacion',
        header: 'Variación promedio',
        size: 140,
        accessorFn: (r) => Number(r.variacion_promedio || 0),
        Cell: ({ row }) => pctVar(row.original.variacion_promedio),
      },
      { accessorKey: 'primer_cambio', header: 'Primer cambio', size: 120 },
      { accessorKey: 'ultimo_cambio', header: 'Último cambio', size: 120 },
      { accessorKey: 'usuario', header: 'Abrió', size: 160 },
      {
        id: 'ver',
        header: '',
        size: 90,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <button
            type="button"
            onClick={() => setMes(row.original.mes)}
            style={{
              border: '1px solid #e3e7ec',
              background: row.original.mes === mes ? '#0f2439' : '#fff',
              color: row.original.mes === mes ? '#fff' : '#0f2439',
              borderRadius: 5,
              padding: '3px 10px',
              fontSize: 11.5,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Ver
          </button>
        ),
      },
    ],
    [mes],
  )

  const colsDetalle = useMemo(
    () => [
      { accessorKey: 'marca', header: 'Marca', size: 100 },
      { accessorKey: 'modelo', header: 'Modelo', size: 230 },
      { accessorKey: 'anio_modelo', header: 'Año', size: 70 },
      {
        id: 'antes',
        header: 'Al abrir el ciclo',
        size: 130,
        accessorFn: (r) => Number(r.precio_al_abrir || 0),
        Cell: ({ row }) =>
          Number(row.original.precio_al_abrir) > 0 ? (
            fmt(row.original.precio_al_abrir)
          ) : (
            <span style={{ color: '#9aa5b1' }}>sin precio</span>
          ),
      },
      {
        id: 'ahora',
        header: 'Vigente hoy',
        size: 130,
        accessorFn: (r) => Number(r.precio_vigente || 0),
        Cell: ({ row }) => <b>{fmt(row.original.precio_vigente)}</b>,
      },
      {
        id: 'var',
        header: 'Variación',
        size: 100,
        accessorFn: (r) => Number(r.variacion_pct || 0),
        Cell: ({ row }) => pctVar(row.original.variacion_pct),
      },
      { id: 'colores', header: 'Colores', size: 80, accessorFn: (r) => Number(r.colores || 0) },
      {
        id: 'veces',
        header: 'Correcciones',
        size: 120,
        accessorFn: (r) => Number(r.veces || 1),
        Cell: ({ row }) =>
          Number(row.original.veces) > 1 ? (
            <span
              style={{ background: '#fff4dd', color: '#7a5300', padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}
            >
              {Number(row.original.veces) - 1} ajuste(s) después
            </span>
          ) : (
            <span style={{ color: '#9aa5b1' }}>carga única</span>
          ),
      },
      { accessorKey: 'ultimo_cambio', header: 'Último cambio', size: 140 },
      { accessorKey: 'usuario', header: 'Usuario', size: 150 },
    ],
    [],
  )

  return (
    <div>
      <div className="precios-card">
        <div className="precios-section-title">Ciclos mensuales de precio</div>
        <div style={{ fontSize: 12.5, color: '#6b7684', marginBottom: 12 }}>
          Cada mes se carga el <b>día 1</b>; las <b>correcciones</b> posteriores se cuentan aparte.
        </div>
        <TablaPrecios columns={colsCiclos} data={ciclos} isLoading={isLoading} pageSize={12} />
      </div>

      {mes && (
        <div className="precios-card">
          <div className="precios-section-title">Detalle del ciclo {mes} — qué se movió y sobre qué</div>
          <TablaPrecios columns={colsDetalle} data={detalle} pageSize={15} />
        </div>
      )}
    </div>
  )
}

export default CiclosTab
